import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import type { IsoCamera } from "./isoCamera";

/** UI-covered bands of the canvas, CSS px: the top bar above the hero shot, the battle card + PLAY + tab bar below. */
export interface HubInsets { readonly top: number; readonly bottom: number }

/** What a DOM chip can be anchored on: the fortress, or an owned tower (by archetype id) on its stand. */
export type HubTarget = "fortress" | { readonly pet: string } | { readonly tower: string };
/** Whether a target is a pet on the lawn (Pets v3: one per pet had). */
export function isPetTarget(t: HubTarget): t is { readonly pet: string } { return typeof t === "object" && "pet" in t; }

/**
 * The hub's still shot (the lobby redesign): the fortress with the owned towers on stands in a crescent round it,
 * framed in the band between the top bar and the battle card. Pure layout and projection maths, unit-tested.
 */
export const HubLayout = {
  /**
   * Tower stands as whole-cell offsets from the fortress cell, in unlock order. The hub camera sits at the rest yaw
   * (45°, never rotated here), where a cell offset (dx, dz) lands (dz − dx)·0.71 cells right of the fortress on
   * screen and (dx + dz)·0.71 cells behind it. So the first two flank it a little in front, right and left; the next
   * two stand straight behind those, far enough up the screen to clear a tier-1 tower's roof; then the two front
   * corners; the last pair stands behind the keep's flanks. A crescent open at the back, as tight as the fortress's
   * own width allows — the shot is framed on it, so every cell it spreads costs the models size on screen. Whole
   * cells, so each one stands on a flagstone pad like a tower on a map (GroundMesh.setTowers).
   *
   * Twelve towers since TOWERS_V2: the next two close the front row at its outer corners (as wide as the flanks, so
   * the shot keeps its width — a phone's portrait frame is width-bound), and the last two stand at the outer back
   * flanks, half a cell wider than the rest and level with the keep (behind it, they vanished behind its roofs).
   */
  Stands: [[-3, 1], [1, -3], [-1, 3], [3, -1], [-3, -1], [-1, -3], [1, 4], [4, 1], [-4, 0], [0, -4], [-1, 4], [4, -1]] as readonly (readonly [number, number])[],
  /**
   * The bands the UI covers before the hub UI says (`HubScene.setFrame`), as shares of the canvas height: the lobby
   * at 393×852 has the top bar down to ~110 px and the battle card from ~470 px (concept.md, "Information
   * architecture").
   */
  DefaultTop: 0.13,
  DefaultBottom: 0.445,
  /** The band the shot frames never gets thinner than this share of the canvas, whatever the insets say. */
  MinBand: 0.2,
  /** DOM chips anchor this far above a model's top on screen, CSS px: clear of a flag tip, close enough to read as its. */
  AnchorGapPx: 4,

  standCell(base: Cell, i: number): Cell | null {
    const s = HubLayout.Stands[i];
    return s ? new Cell(base.x + s[0], base.y + s[1]) : null;
  },

  defaultInsets(height: number): HubInsets {
    return { top: Math.round(height * HubLayout.DefaultTop), bottom: Math.round(height * HubLayout.DefaultBottom) };
  },

  /** Insets as given, but never leaving less than `MinBand` of the canvas between them (bottom gives way first). */
  clampInsets(insets: HubInsets, height: number): HubInsets {
    const top = Math.max(0, Math.min(insets.top, height * (1 - HubLayout.MinBand)));
    const bottom = Math.max(0, Math.min(insets.bottom, height * (1 - HubLayout.MinBand) - top));
    return { top, bottom };
  },

  /** The eight corners of each box: what the shot must fit (a tower's flag tip as much as its plinth). */
  framePoints(boxes: readonly THREE.Box3[]): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    for (const b of boxes) {
      if (b.isEmpty()) continue;
      for (let i = 0; i < 8; i++) out.push(new THREE.Vector3(i & 1 ? b.max.x : b.min.x, i & 2 ? b.max.y : b.min.y, i & 4 ? b.max.z : b.min.z));
    }
    return out;
  },

  /**
   * A model's bounds for framing and anchoring: its meshes only. The chimney smoke's sprites (a `Points`) rise well
   * above the keep, and framing them left the fortress small with a band of sky above it.
   */
  modelBox(root: THREE.Object3D): THREE.Box3 {
    root.updateMatrixWorld(true);
    const box = new THREE.Box3(), part = new THREE.Box3();
    root.traverse((o) => { if ((o as THREE.Mesh).isMesh && o.visible) box.union(part.setFromObject(o)); });
    return box;
  },

  /**
   * Where a chip hangs over a model, CSS px within the viewport: centred over the model's screen box, `AnchorGapPx`
   * above its top-most point. Measured from the projection each time, so it follows any re-frame exactly.
   */
  anchorOver(iso: IsoCamera, box: THREE.Box3): { x: number; y: number } {
    const s = HubLayout.screenBox(iso, HubLayout.framePoints([box]));
    return { x: (s.x0 + s.x1) / 2, y: s.y0 - HubLayout.AnchorGapPx };
  },

  /** Px kept clear between the shot and each edge of its band (the top bar's shadow, the battle card's rim). */
  MarginPx: 20,
  /** Project-measure-correct rounds of `frame`: the error shrinks by an order of magnitude a round. */
  FitRounds: 5,

  /**
   * Frames `points` as large as they fit in the band between the insets, `MarginPx` in from its edges, centred in it
   * (the camera keeps its 35° tilt and rest yaw; only the zoom and the look point move). Worked on the real
   * projection — measure the points' screen box, scale the zoom by how much it over- or under-fills the band, then
   * move the look point by the ground under the remaining offset — so perspective and the insets are exact.
   */
  frame(iso: IsoCamera, points: readonly THREE.Vector3[], insets: HubInsets, width: number, height: number): void {
    iso.setInsets(insets.top, insets.bottom);
    if (points.length === 0) return;
    const m = HubLayout.MarginPx;
    const band = { x0: m, x1: width - m, y0: insets.top + m, y1: height - insets.bottom - m };
    const bx = (band.x0 + band.x1) / 2, by = (band.y0 + band.y1) / 2;
    for (let round = 0; round < HubLayout.FitRounds; round++) {
      const fill = HubLayout.screenBox(iso, points);
      const k = Math.max((fill.x1 - fill.x0) / Math.max(1, band.x1 - band.x0), (fill.y1 - fill.y0) / Math.max(1, band.y1 - band.y0));
      if (k > 0 && Number.isFinite(k)) iso.onPinch(1 / k);
      const box = HubLayout.screenBox(iso, points);
      const from = iso.viewportToGround(bx, by), to = iso.viewportToGround((box.x0 + box.x1) / 2, (box.y0 + box.y1) / 2);
      if (!from || !to) continue;
      const look = iso.lookPoint();
      iso.focus(look.x + to.x - from.x, look.z + to.z - from.z);
    }
  },

  /**
   * Whether a ground point (x, z) at height `y` falls in the band between the insets, `MarginPx` in from its edges, as
   * `camera` sees it — a copy of the default shot's camera, so a pinch since does not change the answer.
   */
  inBand(camera: THREE.Camera, y: number, insets: HubInsets, width: number, height: number): (x: number, z: number) => boolean {
    const m = HubLayout.MarginPx, p = new THREE.Vector3();
    return (x, z) => {
      p.set(x, y, z).project(camera);
      const vx = ((p.x + 1) / 2) * width, vy = ((1 - p.y) / 2) * height;
      return vx >= m && vx <= width - m && vy >= insets.top + m && vy <= height - insets.bottom - m;
    };
  },

  /** The screen box (CSS px within the viewport) the points project to. */
  screenBox(iso: IsoCamera, points: readonly THREE.Vector3[]): { x0: number; x1: number; y0: number; y1: number } {
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    for (const p of points) {
      const v = iso.worldToViewport(p.x, p.y, p.z);
      x0 = Math.min(x0, v.x); x1 = Math.max(x1, v.x); y0 = Math.min(y0, v.y); y1 = Math.max(y1, v.y);
    }
    return { x0, x1, y0, y1 };
  },
} as const;
